// -----------------------------------------------------------------------------
// Supabase Client
//
// SETUP (one-time, ~10 minutes):
// 1. Go to https://supabase.com - New Project - name it "propdesk"
// 2. Wait ~2 min for project to spin up
// 3. Go to Settings - API
// 4. Copy "Project URL" - replace YOUR_SUPABASE_URL below
// 5. Copy "anon public" key - replace YOUR_SUPABASE_ANON_KEY below
// 6. Go to SQL Editor - paste the contents of src/data/schema.sql - Run
// 7. Go to Authentication - Settings - disable "Confirm email" for now (easier dev)
// 8. Deploy to Vercel - data now persists across all sessions!
// -----------------------------------------------------------------------------

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL      || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const IS_CONFIGURED = !!SUPABASE_URL && !!SUPABASE_ANON_KEY;

export const supabase = IS_CONFIGURED
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

// -- Auth ----------------------------------------------------------------------

export const sbSignIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  // Record last login time so reminder system knows they have activated
  if (data.user) {
    supabase.from('profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', data.user.id)
      .then(() => {});  // fire and forget
  }
  return data;
};

export const sbSignUp = async (email, password, metadata) => {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: {
      data: {
        ...metadata,
        // Ensure propertyId is passed as string for the trigger to read
        propertyId: metadata.propertyId ? String(metadata.propertyId) : null,
      }
    },
  });
  if (error) throw error;
  // Return user and session directly for easy destructuring in App.js
  return { user: data.user, session: data.session };
};

export const sbSignOut = async () => {
  try {
    await supabase.auth.signOut();
  } catch {
    // ignore server errors -- session is cleared locally regardless
  }
};

export const sbForgotPassword = async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

export const sbGetSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

// -- Profiles ------------------------------------------------------------------

export const sbGetProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles').select('*').eq('id', userId).maybeSingle();
  // maybeSingle() returns null instead of throwing if no row found
  if (error) throw error;
  return data; // null if profile doesn't exist yet
};

export const sbCreateProfile = async (profile) => {
  const { data, error } = await supabase.from('profiles').insert(profile).select().single();
  if (error) throw error;
  return data;
};

export const sbUpdateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles').update(updates).eq('id', userId).select().single();
  if (error) throw error;
  return data;
};

export const sbGetAllProfiles = async () => {
  const { data, error } = await supabase.from('profiles').select('*');
  if (error) throw error;
  return data || [];
};

// -- Properties ----------------------------------------------------------------

const normalizeProperty = (p) => ({
  id:        p.id,
  name:      p.name,
  address:   p.address,
  units:     p.units,
  image:     p.image,
  regCode:     p.reg_code,
  street:      p.street || '',
  city:        p.city || 'Vancouver',
  province:    p.province || 'BC',
  postalCode:  p.postal_code || '',
  companyId: p.company_id,
});

export const sbGetProperties = async () => {
  const { data, error } = await supabase.from('properties').select('*');
  if (error) throw error;
  return (data || []).map(normalizeProperty);
};

// -- Tickets -------------------------------------------------------------------

export const sbGetTickets = async () => {
  const { data, error } = await supabase
    .from('tickets').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeTicket);
};

export const sbCreateTicket = async (ticket) => {
  const { data, error } = await supabase
    .from('tickets').insert(denormalizeTicket(ticket)).select().single();
  if (error) throw error;
  return normalizeTicket(data);
};

export const sbUpdateTicket = async (id, updates) => {
  const { data, error } = await supabase
    .from('tickets').update(denormalizeTicket(updates)).eq('id', id).select().single();
  if (error) throw error;
  return normalizeTicket(data);
};

// -- Contractors ---------------------------------------------------------------

export const sbGetContractors = async () => {
  const { data, error } = await supabase.from('contractors').select('*');
  if (error) throw error;
  return (data || []).map(normalizeContractor);
};

export const sbCreateContractor = async (contractor) => {
  const { data, error } = await supabase
    .from('contractors').insert(denormalizeContractor(contractor)).select().single();
  if (error) throw error;
  return normalizeContractor(data);
};

// -- Payments ------------------------------------------------------------------

export const sbGetPayments = async () => {
  const { data, error } = await supabase
    .from('payments').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizePayment);
};

export const sbCreatePayment = async (payment) => {
  const { data, error } = await supabase
    .from('payments').insert(denormalizePayment(payment)).select().single();
  if (error) throw error;
  return normalizePayment(data);
};

// -- Field normalizers (snake_case DB - camelCase app) ------------------------

const normalizeTicket = (t) => t ? ({
  id: t.id, propertyId: t.property_id, unit: t.unit,
  renterId: t.renter_id, renter: t.renter_name,
  title: t.title, category: t.category,
  priority: t.priority, status: t.status,
  description: t.description,
  contractorId: t.contractor_id, assignee: t.assignee_name,
  photos: t.photos || [], jobAmount: t.job_amount,
  paid: t.paid || false, date: t.date || t.created_at?.split('T')[0],
}) : null;

const denormalizeTicket = (t) => ({
  ...(t.propertyId  !== undefined && { property_id:   t.propertyId  }),
  ...(t.unit        !== undefined && { unit:          t.unit        }),
  ...(t.renterId    !== undefined && { renter_id:     t.renterId    }),
  ...(t.renter      !== undefined && { renter_name:   t.renter      }),
  ...(t.title       !== undefined && { title:         t.title       }),
  ...(t.category    !== undefined && { category:      t.category    }),
  ...(t.priority    !== undefined && { priority:      t.priority    }),
  ...(t.status      !== undefined && { status:        t.status      }),
  ...(t.description !== undefined && { description:   t.description }),
  ...(t.contractorId!== undefined && { contractor_id: t.contractorId}),
  ...(t.assignee    !== undefined && { assignee_name: t.assignee    }),
  ...(t.photos      !== undefined && { photos:        t.photos      }),
  ...(t.jobAmount   !== undefined && { job_amount:    t.jobAmount   }),
  ...(t.paid        !== undefined && { paid:          t.paid        }),
  ...(t.date        !== undefined && { date:          t.date        }),
});

const normalizeContractor = (c) => c ? ({
  id:              c.id,
  companyId:       c.company_id,
  name:            c.name,
  businessName:    c.business_name || '',
  trade:           c.trade,
  email:           c.email || '',
  phone:           c.phone || '',
  gstNumber:       c.gst_number || '',
  address:         c.address || '',
  city:            c.city || '',
  province:        c.province || '',
  postalCode:      c.postal_code || '',
  notes:           c.notes || '',
  paymentMethod:   c.payment_method || 'etransfer',
  stripeAccountId: c.stripe_account_id,
  stripeOnboarded: c.stripe_onboarded || false,
  totalPaid:       parseFloat(c.total_paid) || 0,
}) : null;

const denormalizeContractor = (c) => ({
  ...(c.companyId       !== undefined && { company_id:        c.companyId       }),
  ...(c.name            !== undefined && { name:              c.name            }),
  ...(c.businessName    !== undefined && { business_name:     c.businessName    }),
  ...(c.trade           !== undefined && { trade:             c.trade           }),
  ...(c.email           !== undefined && { email:             c.email           }),
  ...(c.phone           !== undefined && { phone:             c.phone           }),
  ...(c.gstNumber       !== undefined && { gst_number:        c.gstNumber       }),
  ...(c.address         !== undefined && { address:           c.address         }),
  ...(c.city            !== undefined && { city:              c.city            }),
  ...(c.province        !== undefined && { province:          c.province        }),
  ...(c.postalCode      !== undefined && { postal_code:       c.postalCode      }),
  ...(c.notes           !== undefined && { notes:             c.notes           }),
  ...(c.paymentMethod   !== undefined && { payment_method:    c.paymentMethod   }),
  ...(c.stripeAccountId !== undefined && { stripe_account_id: c.stripeAccountId }),
  ...(c.stripeOnboarded !== undefined && { stripe_onboarded:  c.stripeOnboarded }),
  ...(c.totalPaid       !== undefined && { total_paid:        c.totalPaid       }),
});

const normalizePayment = (p) => p ? ({
  id: p.id, type: p.type,
  ticketId: p.ticket_id, contractorId: p.contractor_id,
  propertyId: p.property_id, renterName: p.renter_name,
  unit: p.unit, amount: parseFloat(p.amount) || 0,
  status: p.status, stripePaymentId: p.stripe_payment_id,
  notes: p.notes, date: p.date || p.created_at?.split('T')[0],
}) : null;

const denormalizePayment = (p) => ({
  ...(p.type            !== undefined && { type:              p.type            }),
  ...(p.ticketId        !== undefined && { ticket_id:         p.ticketId        }),
  ...(p.contractorId    !== undefined && { contractor_id:     p.contractorId    }),
  ...(p.propertyId      !== undefined && { property_id:       p.propertyId      }),
  ...(p.renterName      !== undefined && { renter_name:       p.renterName      }),
  ...(p.unit            !== undefined && { unit:              p.unit            }),
  ...(p.amount          !== undefined && { amount:            p.amount          }),
  ...(p.status          !== undefined && { status:            p.status          }),
  ...(p.stripePaymentId !== undefined && { stripe_payment_id: p.stripePaymentId }),
  ...(p.notes           !== undefined && { notes:             p.notes           }),
  ...(p.date            !== undefined && { date:              p.date            }),
});

// -- Owners --------------------------------------------------------------------

const normalizeOwner = (o) => o ? ({
  id:          o.id,
  companyId:   o.company_id,
  name:        o.name,
  email:       o.email || '',
  phone:       o.phone || '',
  propertyId:  o.property_id,
  mgmtFee:     parseFloat(o.mgmt_fee) || 0,
  stripeLink:  o.stripe_link || '',
  notes:       o.notes || '',
  createdAt:   o.created_at,
}) : null;

const denormalizeOwner = (o) => ({
  ...(o.name        !== undefined && { name:        o.name        }),
  ...(o.email       !== undefined && { email:       o.email       }),
  ...(o.phone       !== undefined && { phone:       o.phone       }),
  ...(o.propertyId  !== undefined && { property_id: o.propertyId  }),
  ...(o.mgmtFee     !== undefined && { mgmt_fee:    o.mgmtFee     }),
  ...(o.stripeLink  !== undefined && { stripe_link: o.stripeLink  }),
  ...(o.notes       !== undefined && { notes:       o.notes       }),
  ...(o.companyId   !== undefined && { company_id:  o.companyId   }),
});

export const sbGetOwners = async () => {
  const { data, error } = await supabase.from('owners').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeOwner);
};

export const sbCreateOwner = async (owner) => {
  const { data, error } = await supabase.from('owners').insert(denormalizeOwner(owner)).select().single();
  if (error) throw error;
  return normalizeOwner(data);
};

export const sbUpdateOwner = async (id, updates) => {
  const { data, error } = await supabase.from('owners').update(denormalizeOwner(updates)).eq('id', id).select().single();
  if (error) throw error;
  return normalizeOwner(data);
};

export const sbDeleteOwner = async (id) => {
  const { error } = await supabase.from('owners').delete().eq('id', id);
  if (error) throw error;
};

// -- Invoices ------------------------------------------------------------------

const normalizeInvoice = (i) => i ? ({
  id:                 i.id,
  companyId:          i.company_id,
  ownerId:            i.owner_id,
  number:             i.number,
  period:             i.period,
  issueDate:          i.issue_date,
  dueDate:            i.due_date,
  items:              i.items || [],
  total:              parseFloat(i.total) || 0,
  status:             i.status || 'Draft',
  notes:              i.notes || '',
  stripePaymentLink:  i.stripe_payment_link || '',
  paidDate:           i.paid_date,
  paymentMethod:      i.payment_method,
  sentAt:             i.sent_at,
  createdAt:          i.created_at,
}) : null;

const denormalizeInvoice = (i) => ({
  ...(i.ownerId            !== undefined && { owner_id:            i.ownerId            }),
  ...(i.number             !== undefined && { number:              i.number             }),
  ...(i.period             !== undefined && { period:              i.period             }),
  ...(i.issueDate          !== undefined && { issue_date:          i.issueDate          }),
  ...(i.dueDate            !== undefined && { due_date:            i.dueDate            }),
  ...(i.items              !== undefined && { items:               i.items              }),
  ...(i.total              !== undefined && { total:               i.total              }),
  ...(i.status             !== undefined && { status:              i.status             }),
  ...(i.notes              !== undefined && { notes:               i.notes              }),
  ...(i.stripePaymentLink  !== undefined && { stripe_payment_link: i.stripePaymentLink  }),
  ...(i.paidDate           !== undefined && { paid_date:           i.paidDate           }),
  ...(i.paymentMethod      !== undefined && { payment_method:      i.paymentMethod      }),
  ...(i.sentAt             !== undefined && { sent_at:             i.sentAt             }),
  ...(i.companyId          !== undefined && { company_id:          i.companyId          }),
});

export const sbGetInvoices = async () => {
  const { data, error } = await supabase.from('invoices').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeInvoice);
};

export const sbCreateInvoice = async (invoice) => {
  const { data, error } = await supabase.from('invoices').insert(denormalizeInvoice(invoice)).select().single();
  if (error) throw error;
  return normalizeInvoice(data);
};

export const sbUpdateInvoice = async (id, updates) => {
  const { data, error } = await supabase.from('invoices').update(denormalizeInvoice(updates)).eq('id', id).select().single();
  if (error) throw error;
  return normalizeInvoice(data);
};

// -- Leases --------------------------------------------------------------------

const normalizeLease = (l) => l ? ({
  id:              l.id,
  renterId:        l.renter_id,
  renterName:      l.renter_name || '',
  renterEmail:     l.renter_email || '',
  propertyId:      l.property_id,
  propertyName:    l.property_name || '',
  unit:            l.unit || '',
  startDate:       l.start_date,
  endDate:         l.end_date,
  monthlyRent:     parseFloat(l.monthly_rent) || 0,
  securityDeposit: parseFloat(l.security_deposit) || 0,
  renewalStatus:   l.renewal_status || 'Active',
  noticeToVacate:  l.notice_to_vacate || false,
  notes:           l.notes || '',
  createdAt:       l.created_at,
}) : null;

const denormalizeLease = (l) => ({
  ...(l.renterId        !== undefined && { renter_id:        l.renterId        }),
  ...(l.renterName      !== undefined && { renter_name:      l.renterName      }),
  ...(l.renterEmail     !== undefined && { renter_email:     l.renterEmail     }),
  ...(l.propertyId      !== undefined && { property_id:      l.propertyId      }),
  ...(l.propertyName    !== undefined && { property_name:    l.propertyName    }),
  ...(l.unit            !== undefined && { unit:             l.unit            }),
  ...(l.startDate       !== undefined && { start_date:       l.startDate       }),
  ...(l.endDate         !== undefined && { end_date:         l.endDate         }),
  ...(l.monthlyRent     !== undefined && { monthly_rent:     l.monthlyRent     }),
  ...(l.securityDeposit !== undefined && { security_deposit: l.securityDeposit }),
  ...(l.renewalStatus   !== undefined && { renewal_status:   l.renewalStatus   }),
  ...(l.noticeToVacate  !== undefined && { notice_to_vacate: l.noticeToVacate  }),
  ...(l.notes           !== undefined && { notes:            l.notes           }),
});

export const sbGetLeases = async () => {
  const { data, error } = await supabase.from('leases').select('*').order('end_date', { ascending: true });
  if (error) throw error;
  return (data || []).map(normalizeLease);
};

export const sbCreateLease = async (lease) => {
  const { data, error } = await supabase.from('leases').insert(denormalizeLease(lease)).select().single();
  if (error) throw error;
  return normalizeLease(data);
};

export const sbUpdateLease = async (id, updates) => {
  const { data, error } = await supabase.from('leases').update(denormalizeLease(updates)).eq('id', id).select().single();
  if (error) throw error;
  return normalizeLease(data);
};

// -- Documents -----------------------------------------------------------------

const normalizeDocument = (d) => d ? ({
  id:          d.id,
  name:        d.name,
  type:        d.type,
  renterId:    d.renter_id,
  renterName:  d.renter_name || '',
  propertyId:  d.property_id,
  propertyName:d.property_name || '',
  notes:       d.notes || '',
  storagePath: d.storage_path,
  fileUrl:     d.file_url,
  fileName:    d.file_name,
  fileSize:    d.file_size,
  fileType:    d.file_type,
  uploadedAt:  d.uploaded_at || d.created_at,
}) : null;

const denormalizeDocument = (d) => ({
  ...(d.name         !== undefined && { name:          d.name         }),
  ...(d.type         !== undefined && { type:          d.type         }),
  ...(d.renterId     !== undefined && { renter_id:     d.renterId     }),
  ...(d.renterName   !== undefined && { renter_name:   d.renterName   }),
  ...(d.propertyId   !== undefined && { property_id:   d.propertyId   }),
  ...(d.propertyName !== undefined && { property_name: d.propertyName }),
  ...(d.notes        !== undefined && { notes:         d.notes        }),
  ...(d.storagePath  !== undefined && { storage_path:  d.storagePath  }),
  ...(d.fileUrl      !== undefined && { file_url:      d.fileUrl      }),
  ...(d.fileName     !== undefined && { file_name:     d.fileName     }),
  ...(d.fileSize     !== undefined && { file_size:     d.fileSize     }),
  ...(d.fileType     !== undefined && { file_type:     d.fileType     }),
  ...(d.uploadedAt   !== undefined && { uploaded_at:   d.uploadedAt   }),
});

export const sbGetDocuments = async () => {
  const { data, error } = await supabase.from('documents').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeDocument);
};

export const sbCreateDocument = async (doc) => {
  const { data, error } = await supabase.from('documents').insert(denormalizeDocument(doc)).select().single();
  if (error) throw error;
  return normalizeDocument(data);
};

export const sbDeleteDocument = async (id) => {
  const { error } = await supabase.from('documents').delete().eq('id', id);
  if (error) throw error;
};

// -- Properties (create) ---------------------------------------------------
export const sbCreateProperty = async (property) => {
  const street  = property.street || property.name;
  const city    = property.city || 'Vancouver';
  const province= property.province || 'BC';
  const address = `${street}, ${city}, ${province}${property.postalCode ? ' ' + property.postalCode : ''}`;

  const { data, error } = await supabase.from('properties').insert({
    company_id:  1,
    name:        property.name || street,
    address,
    street:      street,
    city,
    province,
    postal_code: property.postalCode || null,
    units:       parseInt(property.units) || 1,
    image:       property.image || 'building',
    reg_code:    property.regCode,
  }).select().single();
  if (error) throw error;
  return {
    id:         data.id,
    name:       data.name,
    address:    data.address,
    street:     data.street || '',
    city:       data.city || 'Vancouver',
    province:   data.province || 'BC',
    postalCode: data.postal_code || '',
    units:      data.units,
    image:      data.image,
    regCode:    data.reg_code,
    companyId:  data.company_id,
  };
};

// -- Properties (update) ---------------------------------------------------
export const sbUpdateProperty = async (id, updates) => {
  const { data, error } = await supabase.from('properties').update({
    ...(updates.name       !== undefined && { name:        updates.name       }),
    ...(updates.address    !== undefined && { address:     updates.address    }),
    ...(updates.street     !== undefined && { street:      updates.street     }),
    ...(updates.city       !== undefined && { city:        updates.city       }),
    ...(updates.province   !== undefined && { province:    updates.province   }),
    ...(updates.postalCode !== undefined && { postal_code: updates.postalCode }),
    ...(updates.units      !== undefined && { units:       parseInt(updates.units) || 1 }),
    ...(updates.image      !== undefined && { image:       updates.image      }),
    ...(updates.regCode    !== undefined && { reg_code:    updates.regCode    }),
  }).eq('id', id).select().single();
  if (error) throw error;
  return {
    id:         data.id,
    name:       data.name,
    address:    data.address,
    street:     data.street || '',
    city:       data.city || 'Vancouver',
    province:   data.province || 'BC',
    postalCode: data.postal_code || '',
    units:      data.units,
    image:      data.image,
    regCode:    data.reg_code,
    companyId:  data.company_id,
  };
};

// -- Notices ---------------------------------------------------------------

const normalizeNotice = (n) => n ? ({
  id:           n.id,
  type:         n.type,
  tenantName:   n.tenant_name || '',
  tenantEmail:  n.tenant_email || '',
  propertyName: n.property_name || '',
  property:     n.property_name || '', // alias for template compatibility
  unit:         n.unit || '',
  currentRent:  parseFloat(n.current_rent) || 0,
  newRent:      parseFloat(n.new_rent) || 0,
  rate:         parseFloat(n.rate) || 0,
  effectiveDate:n.effective_date || '',
  generatedOn:  n.generated_on || '',
  noticeHtml:   n.notice_html || '',  // kept for backward compat
  noticeUrl:    n.notice_url || '',
  storagePath:  n.storage_path || '',
  emailSent:    n.email_sent || false,
  emailSentAt:  n.email_sent_at || null,
  createdAt:    n.created_at,
}) : null;

export const sbGetNotices = async () => {
  const { data, error } = await supabase
    .from('notices').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeNotice);
};

export const sbCreateNotice = async (notice) => {
  const { data, error } = await supabase.from('notices').insert({
    company_id:    1,
    type:          notice.type,
    tenant_name:   notice.tenantName   || null,
    tenant_email:  notice.tenantEmail  || null,
    property_name: notice.propertyName || notice.property || null,
    unit:          notice.unit         || null,
    current_rent:  notice.currentRent  || null,
    new_rent:      notice.newRent      || null,
    rate:          notice.rate         || null,
    effective_date:notice.effectiveDate|| null,
    generated_on:  notice.generatedOn  || new Date().toISOString().split('T')[0],
    notice_html:   null,  // HTML stored in R2, not in DB
    notice_url:    notice.noticeUrl    || null,
    storage_path:  notice.storagePath  || null,
    email_sent:    notice.emailSent    || false,
    email_sent_at: notice.emailSent ? new Date().toISOString() : null,
  }).select().single();
  if (error) throw error;
  return normalizeNotice(data);
};

export const sbUpdateNotice = async (id, updates) => {
  const { data, error } = await supabase.from('notices').update({
    ...(updates.emailSent    !== undefined && { email_sent:    updates.emailSent    }),
    ...(updates.emailSentAt  !== undefined && { email_sent_at: updates.emailSentAt  }),
    ...(updates.noticeHtml   !== undefined && { notice_html:   updates.noticeHtml   }),
    ...(updates.noticeUrl    !== undefined && { notice_url:    updates.noticeUrl    }),
    ...(updates.storagePath  !== undefined && { storage_path:  updates.storagePath  }),
  }).eq('id', id).select().single();
  if (error) throw error;
  return normalizeNotice(data);
};

// -- Leads (public — no auth required for insert) --------------------------

export const sbCreateLead = async (lead) => {
  if (!IS_CONFIGURED) return null;
  const { data, error } = await supabase.from('leads').insert({
    name:         lead.name || null,
    email:        lead.email,
    phone:        lead.phone || null,
    properties:   lead.properties || null,
    source:       lead.source || 'calculator',
    current_rent: lead.currentRent ? parseFloat(lead.currentRent) : null,
    notes:        lead.notes || null,
    status:       'new',
  }).select().single();
  if (error) throw error;
  return data;
};

// -- Invite Renter (manager creates account on behalf of renter) -----------

export const sbInviteRenter = async ({ name, email, propertyId, unit }) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password: Math.random().toString(36).slice(-12) + 'Aa1!',
    options: {
      data: {
        name,
        role:       'renter',
        propertyId: propertyId ? String(propertyId) : null,
        unit:       unit || null,
      },
      emailRedirectTo: window.location.origin,
    },
  });
  if (error) throw error;

  if (data.user) {
    const { error: profileError } = await supabase.from('profiles').insert({
      id:          data.user.id,
      role:        'renter',
      name,
      email,
      property_id: propertyId || null,
      unit:        unit || null,
      company_id:  1,
    }).select().single();
    if (profileError && !profileError.message.includes('duplicate')) {
      console.warn('Profile insert warning:', profileError.message);
    }
  }

  return data.user;
};

// -- Ticket Comments -------------------------------------------------------

const normalizeComment = (c) => c ? ({
  id:         c.id,
  ticketId:   c.ticket_id,
  authorName: c.author_name || 'Manager',
  authorRole: c.author_role || 'manager',
  message:    c.message,
  createdAt:  c.created_at,
}) : null;

export const sbGetComments = async (ticketId) => {
  const { data, error } = await supabase
    .from('ticket_comments')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map(normalizeComment);
};

export const sbAddComment = async (ticketId, message, authorName, authorRole) => {
  const { data, error } = await supabase
    .from('ticket_comments')
    .insert({
      ticket_id:   ticketId,
      message,
      author_name: authorName || 'Manager',
      author_role: authorRole || 'manager',
    }).select().single();
  if (error) throw error;
  return normalizeComment(data);
};
